import type { UseQueryResult } from "@tanstack/react-query"
import { useQuery } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type { BatteryConcertApplicationStatusDto } from "../dto/battery-concert-application.dto"
import { batteryProposalService } from "../services/battery-proposal.service"

/**
 * Concert Battery application status. Reading never creates a customer or
 * submits credit; the server refreshes a dispatched application from Concert
 * on each read, so a drawer that must show the latest decision passes
 * `refetchOnMount: "always"`.
 */
export default function useBatteryConcertApplication(
  proposalId: string | undefined,
  {
    enabled = true,
    refetchOnMount = true,
  }: { enabled?: boolean; refetchOnMount?: boolean | `always` } = {},
): UseQueryResult<BatteryConcertApplicationStatusDto> {
  return useQuery({
    queryKey: queryKeys.batteryProposal.concertApplication(proposalId ?? ``),
    queryFn: () => {
      if (!proposalId) {
        throw new Error(`Battery-Only Proposal id is required`)
      }

      return batteryProposalService.getConcertApplication(proposalId)
    },
    enabled: Boolean(proposalId) && enabled,
    retry: 1,
    // Each read may ask Concert for eligibility; the setup steps share one
    // answer for half a minute while the status drawer still forces a refresh.
    staleTime: 30_000,
    refetchOnMount,
    refetchOnWindowFocus: false,
  })
}
